import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import QuizAnswerMode from './QuizAnswerMode';
import QuizReviewMode from './QuizReviewMode';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { enrichQuestionsWithDbFields, mapDbQuestionsToStandard } from '../../utils/questionMapper';
import { getQuestionsCorrectRate, calcDifficultyByCorrectRate, type QuestionCorrectRateInfo } from '../../utils/questionStats';
import { categoryStore } from '../../store/categoryStore';
import { getBeijingTimestamp } from '../../utils/timeUtils';
import { loadKnowledgeTagData, loadTaggedContentKeysPerTag, filterQuestionsByContentKeys, computeContentKey, loadTagMarksForQuestions, buildTagDataFromMarks, extractTagDataFromQuestions, fetchKnowledgeCategories, sortTagsByManagerOrder, type TagDef } from '../../utils/questionTag';
import { computeSelectedKnowledgeTags } from '../../components/KnowledgeTagPills';
import type { Question, Paper, QuizProps } from './types';

export { type QuizProps } from './types';

export default function Quiz(props?: QuizProps) {
  const navigate = useNavigate();
  const { paperId } = useParams<{ paperId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // 模式判断
  const isPreviewMode = searchParams.get('mode') === 'preview';
  const isEditorPreview = searchParams.get('mode') === 'editor-preview' || location.state?.previewData?.isEditorPreview;
  const fromResult = location.state?.fromResult;
  const isProxyView = location.state?.isProxyView;
  const studentName = location.state?.studentName;
  const isPreview = isPreviewMode || isEditorPreview;

  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [paper, setPaper] = useState<Paper | null>(null);
  const [examQuestions, setExamQuestions] = useState<Question[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [customError, setCustomError] = useState<string>(''); // 自定义组卷错误信息
  const [customPaperId, setCustomPaperId] = useState<string>(''); // 自定义组卷唯一试卷ID（每次组卷不同，用于作答次数独立计数）
  const [reviewData, setReviewData] = useState<{
    answers: Record<string, string>;
    score: number;
    timeLeft: number;
    objectiveScore?: number | null;
    objectiveTotalScore?: number | null;
    programmingScore?: number | null;
    questionFeedbacks?: Record<string, { score?: number; feedback?: string }> | null;
    source?: 'practice' | 'class' | 'exam'; // 新增：来源字段
    correctRateMap?: Record<string, QuestionCorrectRateInfo>; // 新增：题目正确率数据
  } | null>(null);
  // 知识点标签（复盘模式答案解析区只读展示）
  const [reviewTags, setReviewTags] = useState<TagDef[]>([]);
  const [reviewTagMarks, setReviewTagMarks] = useState<Record<string, string[]>>({});

  // 初始化数据加载
  useEffect(() => {
    const init = async () => {
      setLoading(true);

      // 自定义组卷模式：根据 customConfig 自己查询抽题
      // 不再依赖 ExamPlatform 传递的 prebuiltQuestions（大数据通过 location.state 传递会丢失）
      const customConfig = (location.state as any)?.customConfig;
      console.log('[Quiz诊断] paperId:', paperId, 'customConfig存在:', !!customConfig, 'fromResult:', fromResult, 'isProxyView:', isProxyView, 'location.state存在:', !!location.state);

      // 回顾/代查模式优先：fromResult 和 isProxyView 不依赖 customConfig，
      // 必须在 customConfig 检查之前判断，否则自定义组卷的 paperId='custom' 会拦截回顾模式
      if (fromResult || isProxyView) {
        console.log('[Quiz诊断] 进入回顾/代查模式，跳过 customConfig 检查');
        // 以下逻辑由后面的 fromResult/isProxyView 分支处理
      } else if (!customConfig && (paperId === 'custom' || !paperId)) {
        // 关键修复：当 customConfig 不存在时（如浏览器刷新导致 location.state 丢失），
        // 直接给出明确错误提示，避免走普通试卷加载逻辑导致"未找到试卷数据"的误导
        // 不再依赖 paperId === 'custom' 判断，因为路由匹配可能让 paperId 不是 'custom'
        console.log('[Quiz诊断] customConfig丢失，显示过期提示');
        setCustomError('组卷数据已过期，请返回重新生成试卷');
        setHasPermission(true);
        setLoading(false);
        return;
      }

      // 只要 customConfig 存在就走自定义组卷逻辑，不依赖 paperId 判断
      if (customConfig) {
        console.log('[Quiz诊断] 进入自定义组卷逻辑, customConfig:', JSON.stringify(customConfig));
        let errorMsg = '';
        try {
          // 1. 获取当前登录用户
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.user) {
            navigate('/login');
            return;
          }
          const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
          setCurrentUser({ id: session.user.id, ...profile });

          // ===== 举一反三模式：按未掌握知识点逐个抽题（每个知识点简单/适中/难题各 1 道，尽量抽满，不足跳过） =====
          if (customConfig.mode === 'targeted') {
            const targetTagIds: string[] = customConfig.targetTagIds || [];
            if (targetTagIds.length === 0) throw new Error('未获取到未掌握知识点，请返回重试');

            // 1. 拉取已发布试卷并按 org/cat/level 匹配
            const { data: papersData, error: papersError } = await supabase
              .from('papers')
              .select('id,organization,category_type,level_text')
              .eq('is_published', true)
              .is('deleted_at', null);
            if (papersError) throw new Error(`查询试卷失败: ${papersError.message}`);
            await categoryStore.init();
            const orgName = customConfig.orgName || '';
            const catName = customConfig.catName || '';
            const levelName = customConfig.levelName || '';
            const matchedPaperIds: string[] = [];
            const skippedByOrg = new Set<string>();
            (papersData || []).forEach((p: any) => {
              // 抽题范围放开：不再限定考评体系（organization），仅按课程分类（编程语言）筛选，
              // 同一编程语言下不同考评体系的题目都进入抽题池
              if (catName) {
                const resolvedCat = categoryStore.getCategoryNameByCode(p.category_type) || p.category_type;
                if (resolvedCat !== catName && p.category_type !== catName) { skippedByOrg.add(p.id); return; }
              }
              if (levelName && p.level_text !== levelName) return;
              matchedPaperIds.push(p.id);
            });
            console.log('[专项抽题-举一反三] 范围放开：跳过考评体系过滤，仅按编程语言筛选。匹配试卷数:', matchedPaperIds.length, 'catName:', catName || '全部', '（被课程分类排除:', skippedByOrg.size, '份）');
            if (matchedPaperIds.length === 0) throw new Error('未找到符合条件的试卷');

            // 2. 查询目标标签的标注数据，构建 tagId -> content_key 指纹集合
            const tagKeyMap: Record<string, Set<string>> = {};
            targetTagIds.forEach(id => { tagKeyMap[id] = new Set(); });
            const MARK_BATCH = 50;
            for (let i = 0; i < matchedPaperIds.length; i += MARK_BATCH) {
              const batch = matchedPaperIds.slice(i, i + MARK_BATCH);
              const { data: marksData, error: marksErr } = await supabase
                .from('paper_question_tag_marks')
                .select('content_key,tag_id')
                .in('paper_id', batch)
                .in('tag_id', targetTagIds);
              if (marksErr) throw new Error(`查询知识点标注失败: ${marksErr.message}`);
              (marksData || []).forEach((m: { content_key: string; tag_id: string }) => {
                if (tagKeyMap[m.tag_id]) tagKeyMap[m.tag_id].add(m.content_key);
              });
            }

            // 3. 轻量拉取匹配试卷的单选题（需 content/title/score 计算指纹）
            const lightQuestions: any[] = [];
            const Q_BATCH = 50;
            const qPromises: Promise<any>[] = [];
            for (let i = 0; i < matchedPaperIds.length; i += Q_BATCH) {
              const batch = matchedPaperIds.slice(i, i + Q_BATCH);
              qPromises.push(
                supabase.from('questions').select('id,question_type,paper_id,content,title,score').in('paper_id', batch)
              );
            }
            const qResults = await Promise.all(qPromises);
            qResults.forEach(({ data: qs, error: qErr }) => {
              if (qErr) console.warn('查询题目批次失败:', qErr.message);
              if (qs) lightQuestions.push(...qs);
            });
            const singlePool = lightQuestions.filter(q => {
              const t = q.question_type || q.type;
              return t === 'single' || t === 'single_choice';
            });

            // 4. 题目按指纹归属到第一个命中的目标标签（跨标签去重）
            const tagQuestionMap: Record<string, any[]> = {};
            targetTagIds.forEach(id => { tagQuestionMap[id] = []; });
            const usedIds = new Set<string>();
            for (const q of singlePool) {
              const key = computeContentKey({ type: q.question_type || q.type, content: q.content, title: q.title, score: q.score ?? undefined });
              for (const tagId of targetTagIds) {
                if (tagKeyMap[tagId].has(key) && !usedIds.has(q.id)) {
                  tagQuestionMap[tagId].push(q);
                  usedIds.add(q.id);
                  break; // 只归入第一个命中标签，保证题目不重复
                }
              }
            }

            // 5. 拉取正确率用于难度分档
            const candidateIds = Object.values(tagQuestionMap).flat().map(q => q.id);
            let correctRateMap: Record<string, any> = {};
            if (candidateIds.length > 0) {
              correctRateMap = (await getQuestionsCorrectRate(candidateIds)) as any;
            }

            const shuffle = <T,>(arr: T[]): T[] => {
              const a = [...arr];
              for (let i = a.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [a[i], a[j]] = [a[j], a[i]];
              }
              return a;
            };

            // 6. 每个标签简单/适中/难题各抽 1 道，不足的难度从剩余题中补足到 3 道；该标签无题则跳过
            const perTagCount = customConfig.perTagCount || 3;
            const pickedLight: any[] = [];
            for (const tagId of targetTagIds) {
              const pool = tagQuestionMap[tagId];
              if (pool.length === 0) continue;
              const groups: Record<'easy' | 'medium' | 'hard', any[]> = { easy: [], medium: [], hard: [] };
              const unknown: any[] = [];
              pool.forEach(q => {
                const rate = correctRateMap[q.id]?.rate;
                if (rate === undefined || rate === null) { unknown.push(q); return; }
                groups[calcDifficultyByCorrectRate(rate)].push(q);
              });
              const picked: any[] = [
                ...shuffle(groups.easy).slice(0, 1),
                ...shuffle(groups.medium).slice(0, 1),
                ...shuffle(groups.hard).slice(0, 1),
              ];
              if (picked.length < perTagCount) {
                const pickedIds = new Set(picked.map(q => q.id));
                const remaining = [...unknown, ...pool.filter(q => !pickedIds.has(q.id))];
                picked.push(...shuffle(remaining).slice(0, perTagCount - picked.length));
              }
              pickedLight.push(...picked.slice(0, perTagCount));
            }
            console.log('[Quiz] 举一反三抽题: 目标标签', targetTagIds.length, '抽到题目', pickedLight.length);
            if (pickedLight.length === 0) {
              throw new Error('当前知识点暂无可抽题目，请稍后再试或更换试卷练习');
            }

            // 7. 拉取全量字段并按抽题顺序重排
            const pickedIds = pickedLight.map(q => q.id);
            const ID_BATCH = 20;
            const fullPromises: Promise<any>[] = [];
            for (let i = 0; i < pickedIds.length; i += ID_BATCH) {
              fullPromises.push(supabase.from('questions').select('*').in('id', pickedIds.slice(i, i + ID_BATCH)));
            }
            const fullResults = await Promise.all(fullPromises);
            let pickedAll: any[] = [];
            fullResults.forEach(({ data: qs }) => { if (qs) pickedAll.push(...qs); });
            const orderMap = new Map(pickedIds.map((id: string, idx: number) => [id, idx]));
            pickedAll.sort((a: any, b: any) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0));

            // 8. 组装试卷并进入答题
            const mappedQuestions = mapDbQuestionsToStandard(pickedAll);
            const totalScore = mappedQuestions.reduce((sum, q) => sum + (q.score || 0), 0);
            const customStamp = getBeijingTimestamp().replace(/[-: ]/g, '').slice(0, 12);
            const uniqueCustomId = `custom-${customStamp}-${Math.random().toString(36).slice(2, 8)}`;
            setCustomPaperId(uniqueCustomId);
            setPaper({
              id: uniqueCustomId,
              title: customConfig.title || '举一反三专项练习',
              questions: mappedQuestions,
              total_score: totalScore,
              organization: customConfig.orgName || '',
              category_type: customConfig.catName || '',
              level_text: customConfig.levelName || '',
            } as any);
            setExamQuestions(mappedQuestions);
            setHasPermission(true);
            setLoading(false);
            return;
          }

          // 2. 根据 customConfig 查询并抽题
          let pickedAll: any[] = [];

          // 2.1 拉取所有已发布试卷
          const { data: papersData, error: papersError } = await supabase
            .from('papers')
            .select('id,organization,category_type,level_text,paper_type,is_free')
            .eq('is_published', true)
            .is('deleted_at', null);
          if (papersError) throw new Error(`查询试卷失败: ${papersError.message}`);
          console.log('[Quiz诊断] 查询到已发布试卷数:', (papersData || []).length);

          // 2.2 本地筛选符合条件的试卷
          const orgName = customConfig.orgName || '';
          const catName = customConfig.catName || '';
          const levelName = customConfig.levelName || '';
          const paperType = customConfig.paperType || 'all';

          await categoryStore.init();
          // 组卷模式：specialized=专项练习（每标签各抽 N 道）；random=随机组卷（整卷总题数）。
          // 历史记录无 mode 字段时按 tagCategory 是否存在兜底判断（提前到试卷筛选前，供过滤策略使用）
          const builderModeEarly: 'specialized' | 'random' = customConfig.mode === 'random' ? 'random' : (customConfig.mode === 'specialized' || customConfig.tagCategory ? 'specialized' : 'random');
          // 抽题范围放开（仅专项练习）：不再限定考评体系（organization），仅按课程分类（编程语言）筛选，
          // 同一编程语言下不同考评体系的题目都进入抽题池；随机组卷保持原三级筛选不变
          const skipOrgFilter = builderModeEarly === 'specialized';
          const matchedPaperIds: string[] = [];
          const orgCoverage = new Set<string>();
          (papersData || []).forEach((p: any) => {
            if (!skipOrgFilter && orgName && p.organization !== orgName) return;
            if (skipOrgFilter && p.organization) orgCoverage.add(p.organization);
            const resolvedCat = categoryStore.getCategoryNameByCode(p.category_type) || p.category_type;
            if (catName && resolvedCat !== catName && p.category_type !== catName) return;
            // 专项练习仅限定编程语言：级别过滤也一并放开（不同级别同语言的题都进入抽题池）
            if (!skipOrgFilter && levelName && p.level_text !== levelName) return;
            if (paperType === 'real' && p.paper_type !== 'real') return;
            if (paperType === 'mock' && p.paper_type !== 'mock') return;
            matchedPaperIds.push(p.id);
          });
          if (skipOrgFilter) {
            console.log('[专项抽题] 范围放开：跳过考评体系与级别过滤，仅按编程语言筛选。匹配试卷数:', matchedPaperIds.length, 'catName:', catName || '全部', '覆盖考评体系:', [...orgCoverage].join('、') || '无');
          }
          console.log('[Quiz诊断] 匹配试卷数:', matchedPaperIds.length, 'orgName:', orgName, 'catName:', catName, 'levelName:', levelName, 'skipOrgFilter:', skipOrgFilter);

          if (matchedPaperIds.length === 0) {
            errorMsg = '未找到符合条件的试卷，请调整筛选条件后重试';
            throw new Error(errorMsg);
          }

          // 2.2.5 专项练习：按知识点标签过滤候选题目（内容指纹机制，不依赖题目 ID）
          let allowedKeys: Set<string> | null = null;
          // 专项练习：每个标签各自的内容指纹集合（「每标签各抽 N 道」语义）
          let perTagKeys: Map<string, Set<string>> | null = null;
          let tagDefs: any[] = [];
          const tagCategory = customConfig.tagCategory || '';
          // 组卷模式：specialized=专项练习（每标签各抽 N 道）；random=随机组卷（整卷总题数）。
          // 历史记录无 mode 字段时按 tagCategory 是否存在兜底判断
          const builderMode: 'specialized' | 'random' = customConfig.mode === 'random' ? 'random' : (customConfig.mode === 'specialized' || tagCategory ? 'specialized' : 'random');
          // 多选：customConfig.tagIds 为数组；兼容历史单选字符串 tagId
          const tagIds: string[] = Array.isArray(customConfig.tagIds)
            ? customConfig.tagIds
            : (customConfig.tagId ? [customConfig.tagId] : []);
          const tagLanguage = customConfig.tagLanguage || '';
          if (tagCategory) {
            // 严格语言过滤：tagLanguage 为空说明课程分类无法识别语言，直接报错而非兜底为 python（防止跨语言抽题）
            if (!tagLanguage) {
              console.error('[Quiz] 专项练习语言缺失: customConfig.tagLanguage 为空，catName=', customConfig.catName, '，拒绝抽题（旧逻辑会兜底为 python 导致跨语言）');
              errorMsg = '当前课程分类无法识别编程语言，无法进行专项练习抽题，请重新选择课程分类';
              throw new Error(errorMsg);
            }
            const { data: kpData, error: kpErr } = await supabase
              .from('knowledge_points')
              .select('id,name,category,language,sort_order')
              .eq('language', tagLanguage)
              .eq('category', tagCategory)
              .order('sort_order', { ascending: true });
            if (kpErr) throw new Error(`查询知识点标签失败: ${kpErr.message}`);
            // 排序与后台知识点标签管理页一致：分类 sort_order + 标签 sort_order，供逐标签抽题顺序与快照携带顺序使用
            try {
              const cats = await fetchKnowledgeCategories();
              tagDefs = sortTagsByManagerOrder((kpData || []) as any[], cats);
            } catch {
              tagDefs = kpData || [];
            }
            const targetTagIds = tagIds.length ? tagIds : tagDefs.map((t: any) => t.id);
            console.log('[Quiz] 专项练习多选标签数:', tagIds.length, '目标标签数:', targetTagIds.length);
            if (targetTagIds.length === 0) {
              errorMsg = '该分类下暂无知识点标签，请更换专项练习分类';
              throw new Error(errorMsg);
            }
            perTagKeys = await loadTaggedContentKeysPerTag(matchedPaperIds, targetTagIds);
            allowedKeys = perTagKeys ? new Set([...perTagKeys.values()].flatMap(s => [...s])) : null;
            if (!allowedKeys || allowedKeys.size === 0) {
              errorMsg = '当前专项练习条件下暂无标注了所选知识点的题目，请调整筛选条件';
              throw new Error(errorMsg);
            }
            console.log('[Quiz诊断] 专项练习过滤：目标标签数', targetTagIds.length, '允许指纹数', allowedKeys.size);
          }

          // 2.3 拉取题目（两阶段加载优化）
          // Phase 1: 只查 id 和 question_type 用于筛选，避免拉取 content/options 等大字段
          // 选了专项练习时需要 content/title/score 计算内容指纹
          const lightQuestions: any[] = [];
          const BATCH = 50;
          const lightSelect = allowedKeys ? 'id,question_type,paper_id,content,title,score' : 'id,question_type,paper_id';
          const batchPromises: Promise<any>[] = [];
          for (let i = 0; i < matchedPaperIds.length; i += BATCH) {
            const batch = matchedPaperIds.slice(i, i + BATCH);
            batchPromises.push(
              supabase.from('questions').select(lightSelect).in('paper_id', batch)
            );
          }
          const batchResults = await Promise.all(batchPromises);
          batchResults.forEach(({ data: qs, error: qErr }, idx) => {
            if (qErr) console.warn('查询题目批次失败:', qErr.message);
            if (qs) lightQuestions.push(...qs);
          });
          console.log('[Quiz诊断] 查询到题目数(轻量):', lightQuestions.length);

          if (lightQuestions.length === 0) {
            errorMsg = '匹配的试卷中暂无题目';
            throw new Error(errorMsg);
          }

          // 专项练习过滤：只保留标注了所选知识点的题目（内容指纹，不依赖题目 ID）
          if (allowedKeys) {
            const filteredLight = filterQuestionsByContentKeys(lightQuestions, allowedKeys);
            console.log('[Quiz诊断] 专项练习过滤后题目数:', filteredLight.length);
            if (filteredLight.length === 0) {
              errorMsg = '当前专项练习条件下暂无标注了所选知识点的题目，请调整筛选条件';
              throw new Error(errorMsg);
            }
            lightQuestions.length = 0;
            lightQuestions.push(...filteredLight);
          }

          // 2.4 按题型分类
          const singlePool = lightQuestions.filter(q => {
            const t = q.question_type || q.type;
            return t === 'single' || t === 'single_choice';
          });
          const judgePool = lightQuestions.filter(q => {
            const t = q.question_type || q.type;
            return t === 'judge';
          });
          const programPool = lightQuestions.filter(q => {
            const t = q.question_type || q.type;
            return t === 'code' || t === 'subjective' || t === 'python_coding' || t === 'scratch_coding' || t === 'cpp_coding';
          });
          console.log('[Quiz诊断] 题型分类: 单选', singlePool.length, '判断', judgePool.length, '编程', programPool.length);

          // 2.5 拉取正确率并按难度筛选
          const allQuestionIds = lightQuestions.map(q => q.id);
          const correctRateMap = await getQuestionsCorrectRate(allQuestionIds);

          const difficultyFilterMap: Record<string, string[]> = {
            '简单题': ['easy'], '中等题': ['medium'], '难题': ['hard'], '均衡': ['easy', 'medium', 'hard']
          };
          const targetDifficulties = difficultyFilterMap[customConfig.difficulty] || ['easy', 'medium', 'hard'];

          const filterByDifficulty = (pool: any[]) => {
            return pool.filter(q => {
              const correctRate = correctRateMap[q.id]?.rate;
              const diff = calcDifficultyByCorrectRate(correctRate);
              return targetDifficulties.includes(diff);
            });
          };

          const shuffle = <T,>(arr: T[]): T[] => {
            const a = [...arr];
            for (let i = a.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [a[i], a[j]] = [a[j], a[i]];
            }
            return a;
          };

          const needSingle = customConfig.singleChoiceCount || 0;
          const needJudge = customConfig.judgeCount || 0;
          const needProgram = (builderMode === 'specialized' || customConfig.tagCategory ? 0 : customConfig.programCount) || 0; // 与 ExamPlatform.validateQuestions 保持一致：专项练习不抽编程题
          const isBalanced = customConfig.difficulty === '均衡';

          const groupByDiff = (pool: any[]) => {
            const groups: Record<'easy' | 'medium' | 'hard', any[]> = { easy: [], medium: [], hard: [] };
            pool.forEach(q => {
              const correctRate = correctRateMap[q.id]?.rate;
              const diff = calcDifficultyByCorrectRate(correctRate);
              groups[diff].push(q);
            });
            return groups;
          };

          const pickByRatio = (pool: any[], total: number): any[] => {
            if (total === 0) return [];
            const groups = groupByDiff(pool);
            const easyNeed = Math.floor(total * 0.3);
            const mediumNeed = Math.floor(total * 0.5);
            const hardNeed = total - easyNeed - mediumNeed;

            const picked: any[] = [];
            picked.push(...shuffle(groups.easy).slice(0, easyNeed));
            picked.push(...shuffle(groups.medium).slice(0, mediumNeed));
            picked.push(...shuffle(groups.hard).slice(0, hardNeed));

            const deficit = total - picked.length;
            if (deficit > 0) {
              const pickedIds = new Set(picked.map(q => q.id));
              const remaining = [
                ...groups.easy.filter(q => !pickedIds.has(q.id)),
                ...groups.medium.filter(q => !pickedIds.has(q.id)),
                ...groups.hard.filter(q => !pickedIds.has(q.id)),
              ];
              picked.push(...shuffle(remaining).slice(0, deficit));
            }
            return picked;
          };

          // 降级策略：难度筛选后题目池为空时，从原始池随机抽取
          // 核心原则：只要原始池有足够题目，组卷就必须成功
          const hasRateData = Object.keys(correctRateMap).length > 0;

          const pickWithFallback = (pool: any[], count: number): any[] => {
            if (count === 0) return [];
            if (!hasRateData) return shuffle(pool).slice(0, count);

            if (isBalanced) {
              const picked = pickByRatio(pool, count);
              if (picked.length >= count) return picked;
              // 补足差额
              const deficit = count - picked.length;
              const pickedIds = new Set(picked.map(q => q.id));
              const remaining = pool.filter(q => !pickedIds.has(q.id));
              picked.push(...shuffle(remaining).slice(0, deficit));
              return picked;
            } else {
              const filtered = filterByDifficulty(pool);
              if (filtered.length > 0) return shuffle(filtered).slice(0, count);
              // 降级：难度筛选后为空，从原始池随机抽取
              console.warn(`难度筛选后题目池为空（目标难度：${targetDifficulties.join('/')}），降级为从原始池随机抽取`);
              return shuffle(pool).slice(0, count);
            }
          };

          let pickedLight: any[];
          if (builderMode === 'specialized' && tagCategory && perTagKeys) {
            // 专项练习：每个知识点标签各抽 N 道，跨标签按题目 id + 内容指纹双重去重（先到先得）；不足按实际数量抽取并提示
            // 同一道题可能存在于多张源卷（题目 id 不同但内容相同），仅按 id 去重挡不住，必须叠加内容指纹去重
            const pickedIdSet = new Set<string>();
            const pickedKeySet = new Set<string>();
            const shortages: string[] = [];
            pickedLight = [];
            const isSingle = (q: any) => ['single', 'single_choice'].includes(q.question_type || q.type);
            const isJudge = (q: any) => (q.question_type || q.type) === 'judge';
            for (const [tagId, keys] of perTagKeys) {
              if (!keys || keys.size === 0) continue;
              const tagName = tagDefs.find((t: any) => t.id === tagId)?.name || '知识点';
              const tagQuestions = filterQuestionsByContentKeys(lightQuestions, keys);
              const tSingle = tagQuestions.filter(isSingle);
              const tJudge = tagQuestions.filter(isJudge);
              const excl = (pool: any[]) => pool.filter(q => !pickedIdSet.has(q.id) && !pickedKeySet.has(computeContentKey({ type: q.question_type || q.type, content: q.content, title: q.title, score: q.score })));
              const picked = [
                ...pickWithFallback(excl(tSingle), needSingle),
                ...pickWithFallback(excl(tJudge), needJudge),
              ];
              picked.forEach(q => {
                pickedIdSet.add(q.id);
                pickedKeySet.add(computeContentKey({ type: q.question_type || q.type, content: q.content, title: q.title, score: q.score }));
              });
              pickedLight.push(...picked);
              if (needSingle > 0 && tSingle.length < needSingle) shortages.push(`「${tagName}」单选题仅有${tSingle.length}道（需${needSingle}道）`);
              if (needJudge > 0 && tJudge.length < needJudge) shortages.push(`「${tagName}」判断题仅有${tJudge.length}道（需${needJudge}道）`);
            }
            console.log('[Quiz诊断] 专项练习 per-tag 抽题完成: 标签数', perTagKeys.size, '总题数', pickedLight.length, '唯一内容指纹数', pickedKeySet.size, '不足提示', shortages.length);
            if (shortages.length > 0) {
              alert(`部分知识点标签题量不足，已按实际数量抽取：\n${shortages.join('\n')}`);
            }
          } else {
            const pickedSingle = pickWithFallback(singlePool, needSingle);
            const pickedJudge = pickWithFallback(judgePool, needJudge);
            const pickedProgram = pickWithFallback(programPool, needProgram);
            pickedLight = [...pickedSingle, ...pickedJudge, ...pickedProgram];
            console.log('[Quiz诊断] 抽题结果: 单选', pickedSingle.length, '判断', pickedJudge.length, '编程', pickedProgram.length, '总计', pickedLight.length);
          }

          if (pickedLight.length === 0) {
            errorMsg = '当前筛选条件下题量不足，请调整筛选条件后重试';
            throw new Error(errorMsg);
          }

          // Phase 2: 只对选中的题目查全量字段（通常 30-50 题，远少于全部题目池）
          const pickedIds = pickedLight.map(q => q.id);
          const ID_BATCH = 20; // in.() URL 长度安全限制
          const fullDataPromises: Promise<any>[] = [];
          for (let i = 0; i < pickedIds.length; i += ID_BATCH) {
            const idBatch = pickedIds.slice(i, i + ID_BATCH);
            fullDataPromises.push(
              supabase.from('questions').select('*').in('id', idBatch)
            );
          }
          const fullDataResults = await Promise.all(fullDataPromises);
          pickedAll = [];
          fullDataResults.forEach(({ data: qs }) => {
            if (qs) pickedAll.push(...qs);
          });
          // 关键修复：in() 批量查询返回顺序与抽题顺序不一致，会导致题型分组错位、题号错乱
          // （如判断题出现在单选题区、题号跳号）。必须按抽题顺序（pickedIds）重排。
          const pickedOrderMap = new Map(pickedIds.map((id, idx) => [id, idx]));
          pickedAll.sort((a: any, b: any) => (pickedOrderMap.get(a.id) ?? 0) - (pickedOrderMap.get(b.id) ?? 0));
          console.log('[Quiz诊断] 全量数据加载完成（已按抽题顺序重排）:', pickedAll.length);

          // 3. 映射为标准格式并组装试卷
          const mappedQuestions = mapDbQuestionsToStandard(pickedAll);
          const totalScore = mappedQuestions.reduce((sum, q) => sum + (q.score || 0), 0);
          console.log('[Quiz诊断] 映射完成, 题目数:', mappedQuestions.length, '总分:', totalScore);

          // 3.5) 携带知识点标注：按源试卷查询抽中题目已有标注（内容指纹为桥），
          //      写入题目快照的 knowledgeTags 字段，随 exam_records 持久化 —— 复盘/代查/记录页均可展示
          let finalQuestions: any[] = mappedQuestions;
          try {
            const carriedMarks = await loadTagMarksForQuestions(pickedAll);
            if (Object.keys(carriedMarks).length > 0) {
              const carriedTags = await buildTagDataFromMarks(carriedMarks);
              finalQuestions = mappedQuestions.map(q => ({
                ...q,
                knowledgeTags: computeSelectedKnowledgeTags(q, carriedTags.tags, carriedMarks),
              }));
              console.log('[Quiz诊断] 知识点标注携带完成:', Object.keys(carriedMarks).length, '道题有标注');
            } else {
              console.log('[Quiz诊断] 源题目无知识点标注，跳过携带');
            }
          } catch (e: any) {
            console.warn('[Quiz诊断] 知识点标注携带失败（不影响组卷）:', e?.message);
          }

          // 每次自定义组卷生成唯一试卷ID（前缀 custom- + 北京时间戳），
          // 交卷时 exam_records.paper_id 使用该 ID，作答次数按试卷独立统计（每次组卷都算首次作答）
          const customStamp = getBeijingTimestamp().replace(/[-: ]/g, '').slice(0, 12);
          const uniqueCustomId = `custom-${customStamp}-${Math.random().toString(36).slice(2, 8)}`;
          setCustomPaperId(uniqueCustomId);
          console.log('[Quiz诊断] 生成自定义组卷唯一试卷ID:', uniqueCustomId);

          setPaper({
            id: uniqueCustomId,
            title: `${customConfig.orgName || '全部'}-${customConfig.catName || '全部'}-${customConfig.levelName || '全部'}-自定义组卷${customStamp}`,
            questions: finalQuestions,
            total_score: totalScore,
            organization: customConfig.orgName || '',
            category_type: customConfig.catName || '',
            level_text: customConfig.levelName || '',
          } as any);
          setExamQuestions(finalQuestions);
          setHasPermission(true);
          setLoading(false);
          console.log('[Quiz诊断] 组卷成功完成');
          return;
        } catch (err: any) {
          console.error('[Quiz诊断] 自定义组卷加载失败:', err);
          setCustomError(err?.message || '自定义组卷加载失败，请稍后重试');
          setHasPermission(true);
          setLoading(false);
          return;
        }
      }

      // 预览模式：加载试卷数据
      if (isPreviewMode || isEditorPreview) {
        // 从 previewData 获取数据（编辑器预览）
        const previewData = location.state?.previewData;
        if (previewData && previewData.questions) {
          setPaper({
            id: previewData.paperId,
            title: previewData.paperTitle,
            questions: previewData.questions,
            total_score: previewData.totalScore,
            category_type: ''
          });
          setExamQuestions(previewData.questions);
        } else if (paperId) {
          // 从数据库加载（试卷列表/草稿列表跳转）
          // 并行查询 papers 和 teacher_papers 表
          const PAPER_FIELDS = 'id,title,subtitle,organization,category_type,level_text,duration,is_free,created_at,year,month,paper_type,source,total_score,remark,single_choice_count,multi_choice_count,judge_count,coding_count,status';
          const [papersResult, teacherPapersResult] = await Promise.all([
            supabase.from('papers').select(PAPER_FIELDS).eq('id', paperId).maybeSingle(),
            supabase.from('teacher_papers').select(PAPER_FIELDS).eq('id', paperId).maybeSingle()
          ]);
          let paperData = papersResult.data;
          let isTeacherPaper = false;
          if (!paperData && teacherPapersResult.data) {
            paperData = teacherPapersResult.data;
            isTeacherPaper = true;
          }

          if (paperData) {
            const questionsTable = isTeacherPaper ? 'teacher_questions' : 'questions';
            const { data: questionsData } = await supabase.from(questionsTable).select('*').eq('paper_id', paperId).order('order_index', { ascending: true });
            const mappedQuestions = mapDbQuestionsToStandard(questionsData || []);
            setPaper({ ...(paperData as any), questions: mappedQuestions, total_score: (paperData as any).total_score || 0 });
            setExamQuestions(mappedQuestions);
          }
        }
        setHasPermission(true);
        setLoading(false);
        return;
      }

      // 代查模式（管理员/教师查看学员答题记录）
      if (isProxyView) {
        // 获取当前登录用户（教师）信息
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
          setCurrentUser({ id: session.user.id, ...profile });
        }

        const resultData = location.state?.resultData;
        if (resultData && resultData.questions && resultData.questions.length > 0) {
          // 从数据库补充布局/缩放字段
          const { data: questionsData } = await supabase
            .from('questions')
            .select('id, content, title, question_type, score, options, correct_answer, explanation, title_image_layout, title_image_widths, title_images, image_position, option_image_widths, option_layout')
            .eq('paper_id', resultData.paperId)
            .order('order_index');

          let enrichedQuestions = resultData.questions;
          if (questionsData) {
            enrichedQuestions = enrichQuestionsWithDbFields(resultData.questions, questionsData);
          }

          setPaper({
            id: resultData.paperId,
            title: resultData.paperTitle,
            questions: enrichedQuestions,
            total_score: resultData.totalScore,
            category_type: ''
          });
          setExamQuestions(enrichedQuestions);
          // 加载题目正确率数据
          const allQuestionIds = enrichedQuestions.map(q => q.id);
          const correctRateMap = await getQuestionsCorrectRate(allQuestionIds);
          // 加载知识点标签（代查复盘答案解析区只读展示）；自定义组卷（custom-*）无 marks 行，
          // 改为从题目快照中提取交卷时携带的 knowledgeTags
          const proxyPaperId = resultData.paperId || paperId || '';
          const tagData = proxyPaperId === 'custom' || proxyPaperId.startsWith('custom-')
            ? extractTagDataFromQuestions(enrichedQuestions)
            : await loadKnowledgeTagData(proxyPaperId);
          setReviewTags(tagData.tags);
          setReviewTagMarks(tagData.marks);

          setReviewData({
            answers: resultData.answers || {},
            score: resultData.score,
            timeLeft: resultData.timeLeft,
            objectiveScore: resultData.objectiveScore,
            objectiveTotalScore: resultData.objectiveTotalScore,
            programmingScore: resultData.programmingScore,
            questionFeedbacks: resultData.questionFeedbacks || null,
            source: (resultData as any).source, // 新增：传递来源字段
            correctRateMap // 新增：传递正确率数据
          });
          setHasPermission(true);
          setLoading(false);
          return;
        }
      }

      // 回顾模式（从答题结果页跳转）
      if (fromResult) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
          setCurrentUser({ id: session.user.id, ...profile });

          // 优先使用 location.state 传递的 resultData（ExamResult 页面已补全传递）
          // 对于自定义组卷（paperId='custom'），Edge Function 无法按 paper_id 查找记录，
          // 所以必须依赖 resultData
          const stateResultData = (location.state as any)?.resultData;
          if (stateResultData && stateResultData.questions && stateResultData.questions.length > 0) {
            // 尝试从数据库补充布局/缩放字段（仅当 paperId 是真实 UUID 时才查询）
            let enrichedQuestions = stateResultData.questions;
            if (paperId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(paperId)) {
              const { data: questionsData } = await supabase
                .from('questions')
                .select('id, content, title, question_type, score, options, correct_answer, explanation, title_image_layout, title_image_widths, title_images, image_position, option_image_widths, option_layout')
                .eq('paper_id', paperId)
                .order('order_index');
              if (questionsData) {
                enrichedQuestions = enrichQuestionsWithDbFields(stateResultData.questions, questionsData);
                // 快照题目 ID 与最新题目对不上时（试卷重建），enrich 会按题序兜底覆盖，此处打印核对信息
                const staleIds = stateResultData.questions.filter((q: any) => !questionsData.some((d: any) => d.id === q.id)).length;
                if (staleIds > 0) {
                  console.log(`[quiz-review] 检测到 ${staleIds} 道快照题目 ID 已失效（试卷重建），已按题序用最新题目数据覆盖`);
                }
              }
            }

            setPaper({
              id: stateResultData.paperId || paperId,
              title: stateResultData.paperTitle || '自定义组卷',
              questions: enrichedQuestions,
              total_score: stateResultData.totalScore || 0,
              category_type: ''
            });
            setExamQuestions(enrichedQuestions);
            // 加载题目正确率数据
            const allQuestionIds = enrichedQuestions.map(q => q.id);
            const correctRateMap = await getQuestionsCorrectRate(allQuestionIds);
            // 加载知识点标签（复盘答案解析区只读展示；自定义组卷从题目快照提取携带的标注，其余查询数据库）
            const reviewPaperId = stateResultData.paperId || paperId || '';
            const tagData = reviewPaperId === 'custom' || reviewPaperId.startsWith('custom-')
              ? extractTagDataFromQuestions(enrichedQuestions)
              : await loadKnowledgeTagData(reviewPaperId);
            setReviewTags(tagData.tags);
            setReviewTagMarks(tagData.marks);

            setReviewData({
              answers: stateResultData.answers || {},
              score: stateResultData.score,
              timeLeft: stateResultData.timeLeft,
              objectiveScore: stateResultData.objectiveScore,
              objectiveTotalScore: stateResultData.objectiveTotalScore,
              programmingScore: stateResultData.programmingScore,
              questionFeedbacks: stateResultData.questionFeedbacks || null,
              source: (stateResultData as any).source,
              correctRateMap
            });
            setHasPermission(true);
            setLoading(false);
            return;
          }

          // 如果 resultData 不可用，尝试从 Edge Function 获取（仅适用于真实试卷 paperId，排除自定义组卷 custom/custom-xxx）
          if (paperId && paperId !== 'custom' && !paperId.startsWith('custom-')) {
            const response = await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              },
              body: JSON.stringify({ action: 'get_records' }),
            });

            const result = await response.json();

            if (result.success && result.records && result.records.length > 0) {
              const currentRecord = result.records.find((r: any) => r.paper_id === paperId);

              if (currentRecord && currentRecord.questions) {
                // 从数据库补充布局/缩放字段
                const { data: questionsData } = await supabase
                  .from('questions')
                  .select('id, content, title, question_type, score, options, correct_answer, explanation, title_image_layout, title_image_widths, title_images, image_position, option_image_widths, option_layout')
                  .eq('paper_id', paperId)
                  .order('order_index');

                let enrichedQuestions = currentRecord.questions;
                if (questionsData) {
                  enrichedQuestions = enrichQuestionsWithDbFields(currentRecord.questions, questionsData);
                }

                setPaper({
                  id: currentRecord.paper_id,
                  title: currentRecord.paper_title,
                  questions: enrichedQuestions,
                  total_score: currentRecord.total_score,
                  category_type: ''
                });
                setExamQuestions(enrichedQuestions);
                // 加载题目正确率数据
                const allQuestionIds = enrichedQuestions.map(q => q.id);
                const correctRateMap = await getQuestionsCorrectRate(allQuestionIds);
                // 加载知识点标签（复盘答案解析区只读展示）；自定义组卷从题目快照提取携带的标注
                const recordPaperId = currentRecord.paper_id || paperId || '';
                const tagData = recordPaperId === 'custom' || recordPaperId.startsWith('custom-')
                  ? extractTagDataFromQuestions(enrichedQuestions)
                  : await loadKnowledgeTagData(recordPaperId);
                setReviewTags(tagData.tags);
                setReviewTagMarks(tagData.marks);

                setReviewData({
                  answers: currentRecord.answers || {},
                  score: currentRecord.score,
                  timeLeft: currentRecord.time_left,
                  objectiveScore: currentRecord.objectiveScore ?? currentRecord.objective_score,
                  objectiveTotalScore: currentRecord.objectiveTotalScore ?? currentRecord.objective_total_score,
                  programmingScore: currentRecord.programmingScore ?? currentRecord.programming_score,
                  questionFeedbacks: currentRecord.question_feedbacks || null,
                  source: currentRecord.source,
                  correctRateMap
                });
                setHasPermission(true);
                setLoading(false);
                return;
              }
            }
          }
        }
      }

      // 收藏练习模式已改为页面内切换，不再使用独立路由
      // 如果有人通过旧链接访问，重定向到收藏页面
      if (paperId === 'favorites-practice') {
        navigate('/favorites', { replace: true });
        return;
      }

      // 正常答题模式：检查登录和权限
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/login');
        return;
      }

      const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
      setCurrentUser({ id: session.user.id, ...profile });

      // 加载试卷数据
      if (paperId) {
        const assignmentId = searchParams.get('assignmentId');

        // 如果有 assignmentId，通过 Edge Function 获取试卷和题目数据（绕过 RLS）
        if (assignmentId && session) {
          try {
            const assignmentResponse = await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              },
              body: JSON.stringify({ action: 'start_assignment', assignmentId }),
            });
            const assignmentResult = await assignmentResponse.json();

            if (assignmentResult.success && assignmentResult.data?.paper && assignmentResult.data?.questions) {
              const paperData = assignmentResult.data.paper;
              const questionsData = assignmentResult.data.questions;
              const enabledTypes = assignmentResult.data.enabledQuestionTypes;

              const mappedQuestions = mapDbQuestionsToStandard(questionsData || []);
              let filteredQuestions = mappedQuestions;
              let effectiveTotalScore = paperData.total_score || 0;

              if (enabledTypes) {
                filteredQuestions = mappedQuestions.filter(q => enabledTypes.includes(q.type));
                effectiveTotalScore = filteredQuestions.reduce((sum, q) => sum + q.score, 0);
              }

              setPaper({ ...paperData, questions: filteredQuestions, total_score: effectiveTotalScore });
              setExamQuestions(filteredQuestions);
              setHasPermission(true);
              setLoading(false);
              return;
            }
          } catch (err) {
            console.error('通过 Edge Function 获取作业数据失败:', err);
            // 失败时回退到直接查询数据库
          }
        }

        // 无 assignmentId 或 Edge Function 失败时，直接从数据库查询
        // 并行查询 papers 和 teacher_papers 表，避免串行等待
        const PAPER_FIELDS = 'id,title,subtitle,organization,category_type,level_text,duration,is_free,created_at,year,month,paper_type,source,total_score,remark,single_choice_count,multi_choice_count,judge_count,coding_count,status';
        const [papersResult, teacherPapersResult] = await Promise.all([
          supabase.from('papers').select(PAPER_FIELDS).eq('id', paperId).maybeSingle(),
          supabase.from('teacher_papers').select(PAPER_FIELDS).eq('id', paperId).maybeSingle()
        ]);
        let paperData = papersResult.data;
        const isTeacherPaper = !paperData && teacherPapersResult.data;
        if (isTeacherPaper) paperData = teacherPapersResult.data;

        if (paperData) {
          const questionsTable = isTeacherPaper ? 'teacher_questions' : 'questions';
          const { data: questionsData } = await supabase.from(questionsTable).select('*').eq('paper_id', paperId).order('order_index', { ascending: true });
          const mappedQuestions = mapDbQuestionsToStandard(questionsData || []);
          setPaper({ ...(paperData as any), questions: mappedQuestions, total_score: (paperData as any).total_score || 0 });
          setExamQuestions(mappedQuestions);
        }
      }

      setHasPermission(true);
      setLoading(false);
    };

    init();
  }, [navigate, paperId, location.state, isPreviewMode, isEditorPreview, fromResult, isProxyView]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        <p className="text-gray-600 text-sm font-medium">正在加载题目，请稍候...</p>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 max-w-md w-full text-center shadow-lg">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">暂无权限</h2>
          <p className="text-gray-600 mb-6">您没有权限参加此考试，请先购买相应套餐。</p>
          <button onClick={() => navigate('/membership')} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            去购买套餐
          </button>
        </div>
      </div>
    );
  }

  // 预览模式 - 使用 QuizAnswerMode (isPreview=true)
  if (isPreview) {
    if (!paper || examQuestions.length === 0) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-500">试卷数据加载中...</div>
        </div>
      );
    }
    return (
      <QuizAnswerMode
        paper={paper}
        examQuestions={examQuestions}
        currentUser={currentUser}
        paperId={paperId}
        isPreview={true}
        isEditorPreview={isEditorPreview}
        onBack={handleBack}
      />
    );
  }

  // 回顾模式（从结果页查看或代查模式）
  if (fromResult || isProxyView) {
    if (!paper || !reviewData) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-500">数据加载失败</div>
        </div>
      );
    }
    return (
      <QuizReviewMode
        paper={paper}
        examQuestions={examQuestions}
        answers={reviewData.answers}
        score={reviewData.score}
        timeLeft={reviewData.timeLeft}
        objectiveScore={reviewData.objectiveScore}
        objectiveTotalScore={reviewData.objectiveTotalScore}
        programmingScore={reviewData.programmingScore}
        questionFeedbacks={reviewData.questionFeedbacks}
        currentUser={currentUser}
        paperId={paperId}
        studentName={studentName}
        source={reviewData.source}
        correctRateMap={reviewData.correctRateMap}
        knowledgeTags={reviewTags}
        tagMarks={reviewTagMarks}
        onBack={handleBack}
      />
    );
  }

  // 正常答题模式
  if (!paper || examQuestions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 max-w-md w-full text-center shadow-lg">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">试卷加载失败</h2>
          <p className="text-gray-600 mb-6 text-sm">
            {customError || '未找到试卷数据，请返回重试或调整筛选条件'}
          </p>
          <button
            onClick={() => navigate('/exam-platform')}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            返回组卷
          </button>
        </div>
      </div>
    );
  }

  return (
    <QuizAnswerMode
      paper={paper}
      examQuestions={examQuestions}
      currentUser={currentUser}
      paperId={customPaperId || paperId}
      isPreview={false}
      isEditorPreview={false}
      onBack={handleBack}
    />
  );
}
